import SendIcon from "@mui/icons-material/Send";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import EntityMember from "_shared/entity-members/entity-member";
import { CanceledError } from "axios";
import {
  getIndividualByUserId,
  getLoadingIndividualUserIds,
  getErrorsLoadingIndividualsByUserIds,
  populateIndividualsByUserIds,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import { marketplaceTeamsApi } from "marketplace/teams/_api/marketplace-team-api";
import Team from "marketplace/teams/team";
import { enqueueSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { useSubmitProposalMutation } from "work/proposals/_api/proposal-api";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import Proposal from "work/proposals/proposal";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import SelectedVendorTeam from "work/work-agreements/teams/vendor-team/selection/selected-vendor-team/selected-vendor-team";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

const Content = styled("section")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "row",
}));
const ProposalActions = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  flexGrow: 1,
  flexWrap: "nowrap",
  columnGap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));
const ProposalActionButton = styled(LoadingButton)(() => ({
  width: "100%",
  whiteSpace: "nowrap",
  "&.Mui-disabled": {
    color: "rgba(0, 0, 0, 0.26) !important",
    backgroundColor: "rgba(0, 0, 0, 0.12) !important",
  },
}));

type ProposalRequestDraftDialogProps = {
  onProposalSubmitted?: (proposal: Proposal) => void;
  onCancel: () => void;
};

export default function ProposalRequestDraftDialog(props: Readonly<ProposalRequestDraftDialogProps>) {
  const { onProposalSubmitted, onCancel } = props;

  // #region Hooks
  const session = useSession();
  const [searchParams] = useSearchParams();
  const dispatch = useAttorneyHubDispatch();
  const preSelectedLeader = EntityMember.fromURLSearchParams(searchParams);
  const preSelectedTeam = SelectedVendorTeam.fromURLSearchParams(searchParams);

  // #region Queries
  const preselectedLeaderIndividual = useSelector(getIndividualByUserId(preSelectedLeader?.userId));
  const loadingIndividualUserIds = useSelector(getLoadingIndividualUserIds());
  const errorsLoadingIndividuals = useSelector(
    getErrorsLoadingIndividualsByUserIds(preSelectedLeader?.userId ? [preSelectedLeader.userId] : [])
  );
  const [submitProposalQuery] = useSubmitProposalMutation();

  // #region State
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [descriptionValue, setDescriptionValue] = React.useState<string>("");

  const proposalBuilder = React.useRef<ProposalBuilder>(new ProposalBuilder({ session }));

  useEffect(() => {
    if (
      preSelectedLeader &&
      !preselectedLeaderIndividual &&
      !loadingIndividualUserIds.includes(preSelectedLeader.userId) &&
      !errorsLoadingIndividuals[preSelectedLeader.userId?.toString() || ""]
    ) {
      dispatch(populateIndividualsByUserIds({ userIds: [preSelectedLeader.userId] }));
    }
  }, [preSelectedLeader, preselectedLeaderIndividual, loadingIndividualUserIds, errorsLoadingIndividuals, dispatch, session]);
  // #endregion

  async function initProposal() {
    try {
      setIsLoading(true);

      if (preSelectedLeader) {
        buildProposalWithPreselectedLeader(preSelectedLeader);
      }
      if (preSelectedTeam) {
        await buildProposalWithPreselectedTeam(preSelectedTeam);
      }
    } catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }
  void initProposal();

  async function buildProposalWithPreselectedLeader(leader: EntityMember) {
    // if (!session.user) {
    //   throw new Error("User is not a client representative");
    // }
    // TODO: Validate that leader has access
    proposalBuilder.current = proposalBuilder.current
      .setClient(session.user?.toEntityMember())
      .setName(new ProjectName("New Proposal"))
      .setVendorTeam(new VendorTeam({ leader: leader, members: [] }));
  }

  async function buildProposalWithPreselectedTeam(
    preSelectedTeam: SelectedVendorTeam
  ) {
    try {
      const teamResult = await dispatch(marketplaceTeamsApi.endpoints.getTeamById.initiate({ teamId: preSelectedTeam.targetTeamId.value }));
      if (!("data" in teamResult)) throw new Error("Failed to load team");
      const teamJSON = teamResult.data;
      if (!teamJSON) throw new Error("Failed to load team");
      const team = Team.fromJSON(teamJSON);
      if (!team) throw new Error("Failed to load team");
      if (!team.leader?.userId || !team.leader?.legalEntityId) return;

      proposalBuilder.current = proposalBuilder.current
        .setClient(session.user?.toEntityMember())
        .setName(new ProjectName("New Proposal"))
        .setVendorTeam(team.toWorkTeam());
    } catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
    }
  }

  async function handleSubmitClicked() {
    try {
      setIsSubmitting(true);
      const issues = ProposalIssues.fromBuilder(proposalBuilder.current);
      if (!issues.canSubmit) return;
      const proposal = proposalBuilder.current.buildNewDraft();
      if (!proposal?.id) throw new Error("Failed to build proposal");
      await submitProposalQuery({ proposalId: proposal.id.value }).unwrap();
      onProposalSubmitted?.(proposal);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to submit proposal", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleDescriptionChanged(event: React.ChangeEvent<HTMLInputElement>) {
    setDescriptionValue(event.target.value);

    proposalBuilder.current = proposalBuilder.current.setDescription(new ProjectDescription(event.target.value));
  }

  const issues = ProposalIssues.fromBuilder(proposalBuilder.current);

  return (
    <>
      <Content>
        {isLoading && <Loader />}
        {!isLoading && (
          <TextField
            fullWidth
            placeholder="Enter a brief description of what you need"
            value={descriptionValue}
            onChange={handleDescriptionChanged}
            onKeyDown={(event) => {
              if (event.key === KeyboardKey.Enter) {
                void handleSubmitClicked();
              }
            }}
          />
        )}
      </Content>
      <ProposalActions>
        <ProposalActionButton
          variant="contained"
          color="primary"
          startIcon={<SendIcon />}
          loading={isSubmitting}
          disabled={isLoading || !issues.canSubmit}
          onClick={handleSubmitClicked}
        >
          Submit
        </ProposalActionButton>
        <ProposalActionButton variant="contained" loading={false} disabled={isLoading} onClick={onCancel}>
          Cancel
        </ProposalActionButton>
      </ProposalActions>
    </>
  );
}
